// MAX auto-derivation — the RAISE half.
//
// Split out of pr.ts (SP-PR), which had grown to 499 lines: one under the
// repo's hard cap, i.e. the next touch of any kind would have broken it.
// pr.ts keeps PR DETECTION (rep-max buckets, per-set and per-session PR
// checks); this module keeps the logic that proposes RAISING a stored MAX.
// The two halves are one-directional: this file reads pr.ts, never the
// reverse. `maxRegression.ts` is this module's mirror — it proposes pulling
// a MAX down.
//
// The rules that govern what lives here, moved with the code they document
// (they used to sit in pr.ts's header, where a reader of this file would
// never see them):
//
//   * MAX auto-suggest uses a conservative bias: `suggestedKg = max(
//     weight_actual, e1RM × 0.95)` — slightly under the theoretical 1RM
//     ceiling because Antenore's MAX is a *training cap*, not a maxed
//     attempt. Pre-trigger threshold is `e1RM > currentMax × 1.02` to
//     avoid suggesting on noise.
//
//   * First-time-exercise: when no MAX exists yet, the algorithm does NOT
//     auto-seed. Manual entry first, auto-derive only after. (Q4-track4
//     open question: chose Option B 2026-05-06 — Antenore can flip to A
//     by removing the early-return on `currentMaxKg == null` below.)
//
//   * Reps above the per-pattern trust cap are informational only: they
//     don't trigger MAX suggestions. The table itself
//     (`REP_TRUST_CAP_BY_PATTERN`) stays in pr.ts — the base module both
//     MAX halves import, and which imports neither of them back — even
//     though this file is its only reader today. Only the fallback used
//     by this call site lives here.

import {
  epley,
  isPREligibleSet,
  REP_TRUST_CAP_BY_PATTERN,
  roundToHalfKg,
} from "./pr.js";
import type { MovementPattern } from "./schemas/muscle.js";
import type { Workout, WorkoutSet } from "./schemas/workout.js";

// Fallback when an exercise has no movementPattern in the catalog.
// Sibling of REP_TRUST_CAP_BY_PATTERN (pr.ts), which stays there as the
// shared base of the MAX domain; this default is read only by the two
// suggest functions below, so it lives here.
const DEFAULT_REP_TRUST_CAP = 8;

// Type-narrowed eligibility for `suggestMaxUpdate` — keeps the
// existing MAX-suggest flow weighted-only since `epley()` needs a
// real load and the MAX surface (UI + DDB row) is keyed on absolute
// kg, not "extra".
const isMaxSuggestibleSet = (
  s: WorkoutSet,
): s is Extract<WorkoutSet, { type: "weighted" }> => {
  return isPREligibleSet(s) && s.type === "weighted";
};

// MAX auto-suggest result. Null when no suggestion is warranted.
export type MaxSuggestion = {
  suggestedKg: number; // user-presentable (already rounded to 0.5 kg)
  e1RM: number; // raw e1RM for transparency
  fromSet: { weightKg: number; reps: number };
  // Present when this suggestion would replace an existing auto-derived
  // MAX (re-derive flow). Lets the review UI show "current → suggested"
  // and only ships when re-deriving over a stale seed (never a manual one).
  replacesKg?: number;
};

// Per-set MAX auto-suggest. Returns null when:
//   - currentMaxKg is null/undefined (first-time-exercise: see file
//     header — Option B, manual seed required first)
//   - the set isn't MAX-suggestible: `isMaxSuggestibleSet` requires
//     weighted, reps >= 1, weight > 0. A `fail` set is NOT excluded — it
//     is valid evidence under the AP-0p model, same as PR detection.
//   - reps exceeds the per-pattern trust cap (informational only)
//   - the projected e1RM doesn't beat current MAX by ≥ 2%
//
// `pattern` defaults to "other" when the exercise has no movementPattern
// set in the catalog. The default trust cap (8) is conservative.
export const suggestMaxUpdate = (params: {
  set: WorkoutSet;
  pattern: MovementPattern | null | undefined;
  currentMaxKg: number | null | undefined;
}): MaxSuggestion | null => {
  if (params.currentMaxKg == null) return null;
  const set = params.set;
  // MAX suggest stays weighted-only — epley() needs a real lifted load
  // and Maxes UI is keyed on absolute kg. Bodyweight progressions get
  // surfaced through isSetPR / prsFromHistory instead.
  if (!isMaxSuggestibleSet(set)) return null;
  const cap =
    (params.pattern && REP_TRUST_CAP_BY_PATTERN[params.pattern]) ??
    DEFAULT_REP_TRUST_CAP;
  if (set.reps > cap) return null;
  const e = epley(set.weight, set.reps);
  // Threshold: 2% above current to avoid noise (a +0.5 kg PR on a 200 kg
  // squat is real but not worth a UI interruption — the underlying PR
  // record still gets stored; only the *suggestion* is gated).
  if (e <= params.currentMaxKg * 1.02) return null;
  // Conservative: never recommend above the actual lifted weight × small
  // margin. e1RM × 0.95 trims the theoretical-1RM optimism back into
  // training-cap territory; max() with the actual weight ensures the
  // suggestion is at least what they just performed.
  const suggested = Math.max(set.weight, e * 0.95);
  const rounded = roundToHalfKg(suggested);
  // Final gate: even after the e1RM threshold passes, the conservative
  // trim + round-to-0.5 can collapse the suggestion onto the current
  // MAX (e.g. current=86, e1RM=90.4, suggested=85.9 → round=86 = current).
  // Skip the pill when there's nothing to actually bump. 1.67.3 fix
  // after Antenore's bench-press 5×77.5 re-edit kept proposing 86→86.
  if (rounded <= params.currentMaxKg) return null;
  return {
    suggestedKg: rounded,
    e1RM: e,
    fromSet: { weightKg: set.weight, reps: set.reps },
  };
};

// Whole-workout suggestion: walks every entry, returns the highest-merit
// suggestion per exercise (if any). Used by the post-save UX so the user
// sees one pill per exercise, not one per set.
export const suggestMaxUpdatesForWorkout = (params: {
  workout: Workout;
  exercisePatterns: Record<string, MovementPattern | null | undefined>;
  currentMaxes: Record<string, number | null | undefined>;
}): Record<string, MaxSuggestion> => {
  const out: Record<string, MaxSuggestion> = {};
  for (const entry of params.workout.entries) {
    const exId = entry.exerciseId;
    const pattern = params.exercisePatterns[exId];
    const currentMaxKg = params.currentMaxes[exId];
    let best: MaxSuggestion | null = null;
    for (const set of entry.sets) {
      const sugg = suggestMaxUpdate({ set, pattern, currentMaxKg });
      if (sugg && (!best || sugg.suggestedKg > best.suggestedKg)) {
        best = sugg;
      }
    }
    if (best) out[exId] = best;
  }
  return out;
};
